import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const INTER = '"Inter", sans-serif';

const NEAR_BLACK  = '#1C1C1E';
const NEAR_BLACK2 = '#3A3A3C';
const BLUE        = '#3B82F6';
const BLUE_DARK   = '#2563EB';
const BLUE_LIGHT  = '#93C5FD';
const BG          = '#F5F6F8';
const BG2         = '#EDF0F3';
const DIVIDER     = '#E4E7EB';
const GREY        = '#6B7280';

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },

  palette: {
    primary: {
      main: NEAR_BLACK,
      light: NEAR_BLACK2,
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: BLUE,
      light: BLUE_LIGHT,
      dark: BLUE_DARK,
      contrastText: '#FFFFFF',
    },
    background: {
      default: BG,
      paper: '#FFFFFF',
    },
    text: {
      primary: NEAR_BLACK,
      secondary: GREY,
    },
    divider: DIVIDER,
    error:   { main: '#DC2626' },
    warning: { main: '#D97706' },
    info:    { main: '#2563EB' },
    success: { main: '#16A34A' },
  },

  typography: {
    fontFamily: INTER,
    h1: {
      fontFamily: INTER,
      fontWeight: 800,
      fontSize: '3.5rem',
      lineHeight: 1.1,
      letterSpacing: '-0.03em',
      color: NEAR_BLACK,
    },
    h2: {
      fontFamily: INTER,
      fontWeight: 800,
      fontSize: '2.5rem',
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
      color: NEAR_BLACK,
    },
    h3: {
      fontFamily: INTER,
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      color: NEAR_BLACK,
    },
    h4: {
      fontFamily: INTER,
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      color: NEAR_BLACK,
    },
    h5: {
      fontFamily: INTER,
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
      color: NEAR_BLACK,
    },
    h6: {
      fontFamily: INTER,
      fontWeight: 700,
      fontSize: '1rem',
      lineHeight: 1.5,
      color: NEAR_BLACK,
    },
    body1: {
      fontWeight: 400,
      color: NEAR_BLACK,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontWeight: 400,
      color: GREY,
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    subtitle1: {
      fontWeight: 600,
      color: NEAR_BLACK,
      fontSize: '1rem',
      lineHeight: 1.75,
    },
    subtitle2: {
      fontWeight: 500,
      color: GREY,
      fontSize: '0.875rem',
      lineHeight: 1.57,
    },
    caption: {
      fontWeight: 500,
      color: GREY,
      fontSize: '0.75rem',
      lineHeight: 1.66,
    },
    overline: {
      fontWeight: 600,
      color: BLUE,
      fontSize: '0.7rem',
      lineHeight: 1.6,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    button: {
      fontFamily: INTER,
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.875rem',
      lineHeight: 1.57,
      letterSpacing: '0.01em',
    },
  },

  shape: {
    borderRadius: 12,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          backgroundColor: BG,
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 50,
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '10px 28px',
          transition: 'filter 0.2s ease, background-color 0.2s ease',
          letterSpacing: '0.01em',
          '@media (max-width: 599px)': {
            padding: '8px 16px',
            fontSize: '0.8rem',
          },
          '&:hover': {
            filter: 'brightness(0.88)',
          },
        },
        containedPrimary: {
          backgroundColor: NEAR_BLACK,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: NEAR_BLACK,
          },
          '&:disabled': {
            backgroundColor: '#D1D5DB',
            color: '#9CA3AF',
          },
        },
        containedSecondary: {
          backgroundColor: BLUE,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: BLUE,
          },
          '&:disabled': {
            backgroundColor: '#BFDBFE',
            color: '#9CA3AF',
          },
        },
        outlinedPrimary: {
          borderColor: NEAR_BLACK,
          color: NEAR_BLACK,
          '&:hover': {
            backgroundColor: 'rgba(28, 28, 30, 0.04)',
            borderColor: NEAR_BLACK,
          },
        },
        outlinedSecondary: {
          borderColor: BLUE,
          color: BLUE_DARK,
          '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.06)',
            borderColor: BLUE_DARK,
          },
        },
        textPrimary: {
          color: NEAR_BLACK,
          '&:hover': {
            backgroundColor: 'rgba(28, 28, 30, 0.06)',
          },
        },
        text: {
          '&:hover': {
            filter: 'none',
          },
        },
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: 'none',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 20px rgba(0,0,0,0.06)',
        },
      },
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: 'none',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 20px rgba(0,0,0,0.06)',
          transition: 'box-shadow 0.25s ease',
          '&:hover': {
            boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
          },
        },
      },
    },

    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: NEAR_BLACK,
          borderRadius: 0,
          borderBottom: `1px solid ${DIVIDER}`,
          boxShadow: 'none',
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          paddingRight: '16px',
          paddingLeft: '16px',
          '@media (min-width: 600px)': {
            paddingRight: '24px',
            paddingLeft: '24px',
          },
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: BG2,
            transition: 'background-color 0.2s ease',
            '& fieldset': {
              borderColor: DIVIDER,
            },
            '&:hover': {
              backgroundColor: '#E4E7EB',
              '& fieldset': {
                borderColor: '#C8CDD3',
              },
            },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: BLUE,
                borderWidth: 2,
              },
            },
          },
          '& .MuiOutlinedInput-input': {
            padding: '12px 14px',
          },
          '& .MuiInputLabel-root': {
            color: GREY,
            fontSize: '0.875rem',
            '&.Mui-focused': {
              color: BLUE_DARK,
            },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: BG2,
          transition: 'background-color 0.2s ease',
          '& fieldset': {
            borderColor: DIVIDER,
          },
          '&:hover': {
            backgroundColor: '#E4E7EB',
            '& fieldset': {
              borderColor: '#C8CDD3',
            },
          },
          '&.Mui-focused': {
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: BLUE,
              borderWidth: 2,
            },
          },
        },
        input: {
          padding: '12px 14px',
          fontVariantNumeric: 'tabular-nums',
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: GREY,
          fontSize: '0.875rem',
          '&.Mui-focused': {
            color: BLUE_DARK,
          },
        },
      },
    },

    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          color: NEAR_BLACK,
          fontWeight: 600,
          textDecoration: 'none',
          transition: 'color 0.2s ease',
          '&:hover': {
            color: BLUE_DARK,
          },
        },
      },
    },

    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6',
          subtitle1: 'h2', subtitle2: 'h3',
          body1: 'p', body2: 'p',
        },
      },
      styleOverrides: {
        root: {
          fontVariantNumeric: 'tabular-nums',
        },
      },
    },

    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '16px',
          paddingRight: '16px',
          '@media (min-width: 600px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid',
        },
        standardError: {
          borderColor: '#FECACA',
          backgroundColor: '#FEF2F2',
          color: '#7F1D1D',
        },
        standardWarning: {
          borderColor: '#FDE68A',
          backgroundColor: '#FFFBEB',
          color: '#78350F',
        },
        standardInfo: {
          borderColor: '#BFDBFE',
          backgroundColor: '#EFF6FF',
          color: '#1E3A5F',
        },
        standardSuccess: {
          borderColor: '#BBF7D0',
          backgroundColor: '#F0FDF4',
          color: '#14532D',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          fontWeight: 600,
          fontSize: '0.75rem',
        },
        colorPrimary: {
          backgroundColor: NEAR_BLACK,
          color: '#FFFFFF',
        },
        colorSecondary: {
          backgroundColor: BLUE,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: BLUE_DARK,
          },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          height: 6,
          backgroundColor: DIVIDER,
        },
        bar: {
          borderRadius: 3,
          backgroundColor: BLUE,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: DIVIDER,
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: NEAR_BLACK,
          color: '#FFFFFF',
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 8,
          border: `1px solid ${BLUE}`,
          padding: '8px 12px',
        },
        arrow: {
          color: NEAR_BLACK,
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontFamily: INTER,
            fontWeight: 700,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            borderBottom: `2px solid ${DIVIDER}`,
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${DIVIDER}`,
          padding: '14px 16px',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:nth-of-type(even)': {
            backgroundColor: BG,
          },
          '&:hover': {
            backgroundColor: BG2,
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 24px 80px rgba(0,0,0,0.12)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: INTER,
          fontWeight: 700,
          fontSize: '1.25rem',
          color: NEAR_BLACK,
          paddingBottom: 8,
        },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          textTransform: 'none',
          fontWeight: 500,
          border: `1px solid ${DIVIDER}`,
          '&.Mui-selected': {
            backgroundColor: NEAR_BLACK,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: NEAR_BLACK2,
            },
          },
        },
      },
    },

    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          gap: 4,
        },
        grouped: {
          '&:not(:first-of-type)': {
            borderRadius: 50,
            borderLeft: `1px solid ${DIVIDER}`,
          },
          '&:first-of-type': {
            borderRadius: 50,
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: INTER,
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          backgroundColor: '#FFFFFF',
          borderRadius: 16,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: BG2,
            borderBottom: `2px solid ${DIVIDER}`,
            fontFamily: INTER,
            fontWeight: 700,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: GREY,
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${DIVIDER}`,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: BG2,
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `2px solid ${DIVIDER}`,
            backgroundColor: BG2,
          },
        },
      },
    },
  },
});

export default responsiveFontSizes(theme);
